/**
 * Runtime validation and defaults for the save document.
 *
 * The runtime half of `save.ts`, same split as `level-guards.ts` is to
 * `level.ts` and for the same reason (see that file's header): the validator is
 * what keeps the type from drifting from what a saved document can actually
 * contain.
 *
 * `Fault`, `Validated` and the `Checker` shape are duplicated from
 * `level-guards.ts` rather than imported. The two schemas have nothing to do
 * with each other beyond both being validated documents, and coupling them to
 * share ten lines would make an unrelated schema change ripple into this file
 * for no reason.
 */

import type { GameProgress, InProgress, LevelRecord, SaveDocument, Settings } from './save';
import { SAVE_SCHEMA_VERSION } from './save';

export interface Fault {
  path: string;
  problem: string;
}

export type Validated<T> = { ok: true; value: T } | { ok: false; faults: Fault[] };

export function describeFaults(faults: Fault[]): string {
  return faults
    .map((fault) => (fault.path ? `${fault.path}: ${fault.problem}` : fault.problem))
    .join('; ');
}

export type VersionVerdict = 'ok' | 'too-new' | 'too-old' | 'unreadable';

/**
 * `PROG-023` and `PROG-024`, the save-document analogue of `DATA-042`/`DATA-043`
 * in `level-guards.ts`. Checked before full validation for the same reason: a
 * newer-schema document is expected to fail shape validation (it may have grown
 * fields), and that failure must not be reported as corruption.
 */
export function checkSaveSchemaVersion(version: unknown): VersionVerdict {
  if (typeof version !== 'number' || !Number.isInteger(version)) return 'unreadable';
  if (version > SAVE_SCHEMA_VERSION) return 'too-new';
  if (version < SAVE_SCHEMA_VERSION) return 'too-old';
  return 'ok';
}

export type SaveMigration = (raw: Record<string, unknown>) => Record<string, unknown>;

/**
 * `1 -> 2`, sprint-8a stage 3, the **first real migration** -- `PROG-023`'s
 * and `PROG-025`'s own tests proved the mechanism in Sprint 2 against a
 * synthetic table because no real prior version existed yet to migrate.
 * Drops `stars` and adds `completedGoalSets` (empty) and `bestTimeMs`
 * (`null`) on every `LevelRecord` in every game, per `SaveDocument`'s own
 * shape -- SDD-003 "Stars and completion" has the reasoning for why the
 * field, not just its value, changed. Loses nothing a player can see except
 * the star count itself: `completed` and `bestScore` cross unchanged, and
 * the star count is derived from `completedGoalSets`, never stored, so
 * dropping the old scalar loses a cache, not data.
 *
 * Malformed input (a `games`/`levels`/level-record entry not shaped as
 * expected) is passed through unchanged at whatever level stopped matching,
 * rather than thrown on -- `validateSaveDocument`, run immediately after
 * every migration by `platform/storage.ts`'s own caller, is what actually
 * decides whether the result is acceptable; a migration that also validates
 * would be deciding that question twice, in two different ways, and the
 * two could disagree.
 */
function migrateV1ToV2(raw: Record<string, unknown>): Record<string, unknown> {
  const games = raw.games;
  if (!isRecord(games)) return raw;

  const migratedGames: Record<string, unknown> = {};
  for (const [gameId, progress] of Object.entries(games)) {
    if (!isRecord(progress)) {
      migratedGames[gameId] = progress;
      continue;
    }
    const levels = progress.levels;
    if (!isRecord(levels)) {
      migratedGames[gameId] = progress;
      continue;
    }
    const migratedLevels: Record<string, unknown> = {};
    for (const [levelId, record] of Object.entries(levels)) {
      if (!isRecord(record)) {
        migratedLevels[levelId] = record;
        continue;
      }
      const migrated: Record<string, unknown> = { ...record };
      delete migrated.stars;
      migrated.completedGoalSets = [];
      migrated.bestTimeMs = null;
      migratedLevels[levelId] = migrated;
    }
    migratedGames[gameId] = { ...progress, levels: migratedLevels };
  }
  return { ...raw, games: migratedGames };
}

/**
 * `2 -> 3`, sprint-8b stage 1. `completedGoalSets` changes element type,
 * not just value -- a positional index into `Level.optionalGoals` becomes a
 * content-derived `goalSetKey` string (`@contracts/level`'s own doc comment
 * has the reasoning for the change itself). This migration cannot compute a
 * real key for an existing numeric entry: it has `Record<string, unknown>`
 * and no access to level files, so it has no `Goal[]` to build a
 * `goalSetKey` from. Dropping the entries is the obvious move and
 * `PROG-025` forbids it -- "the shell shall never silently discard
 * progress." Instead each numeric entry becomes an opaque `legacy:<n>`
 * string: nothing is discarded, and the prefix guarantees it can never
 * collide with a real `goalSetKey` output, which always starts with a goal
 * kind name (`score:`, `collect:`, ...), never `legacy:`. A legacy entry
 * therefore matches nothing and is pruned the first time the level is
 * replayed with its real `optionalGoals` in hand (`recordCompletion`,
 * `shell/completion.ts`) -- temporary, not permanent, without this
 * migration ever needing to know that.
 *
 * Already-string entries pass through unchanged, defensively -- this
 * version has shipped no writer that would produce one, but a migration
 * that assumes its own input is well-typed is exactly the kind of claim
 * `validateSaveDocument`, run immediately after by every real caller, is
 * supposed to be the one to make.
 */
function migrateV2ToV3(raw: Record<string, unknown>): Record<string, unknown> {
  const games = raw.games;
  if (!isRecord(games)) return raw;

  const migratedGames: Record<string, unknown> = {};
  for (const [gameId, progress] of Object.entries(games)) {
    if (!isRecord(progress)) {
      migratedGames[gameId] = progress;
      continue;
    }
    const levels = progress.levels;
    if (!isRecord(levels)) {
      migratedGames[gameId] = progress;
      continue;
    }
    const migratedLevels: Record<string, unknown> = {};
    for (const [levelId, record] of Object.entries(levels)) {
      if (!isRecord(record)) {
        migratedLevels[levelId] = record;
        continue;
      }
      const sets = record.completedGoalSets;
      const migrated: Record<string, unknown> = { ...record };
      if (Array.isArray(sets)) {
        migrated.completedGoalSets = (sets as unknown[]).map((entry) =>
          typeof entry === 'number' ? `legacy:${String(entry)}` : entry,
        );
      }
      migratedLevels[levelId] = migrated;
    }
    migratedGames[gameId] = { ...progress, levels: migratedLevels };
  }
  return { ...raw, games: migratedGames };
}

/**
 * Keyed by the version a migration upgrades *from*. Add an entry here, never
 * edit an old one, when the shape changes again.
 */
export const SAVE_MIGRATIONS: Readonly<Record<number, SaveMigration>> = {
  1: migrateV1ToV2,
  2: migrateV2ToV3,
};

/**
 * Applies registered migrations one version at a time until the document
 * reaches `SAVE_SCHEMA_VERSION`, or stops if a version in the chain has none
 * registered. The second case is a build that shipped a schema bump without a
 * migration for it -- a bug, not a state worth designing a message for -- so the
 * caller treats it the same as an unreadable document (`PROG-025`: retained, not
 * discarded, rather than half-applied).
 */
export function migrateSaveDocument(
  raw: Record<string, unknown>,
  fromVersion: number,
  migrations: Readonly<Record<number, SaveMigration>> = SAVE_MIGRATIONS,
): { ok: true; value: Record<string, unknown> } | { ok: false } {
  let doc = raw;
  let version = fromVersion;
  while (version < SAVE_SCHEMA_VERSION) {
    const migration = migrations[version];
    if (!migration) return { ok: false };
    doc = migration(doc);
    version += 1;
  }
  return { ok: true, value: doc };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'an array';
  if (typeof value === 'string') return `the string ${JSON.stringify(value)}`;
  if (typeof value === 'number') return `the number ${String(value)}`;
  return `a value of type ${typeof value}`;
}

class Checker {
  readonly faults: Fault[] = [];

  add(path: string, problem: string): void {
    this.faults.push({ path, problem });
  }

  bool(value: unknown, path: string): boolean | null {
    if (typeof value !== 'boolean') {
      this.add(path, `expected a boolean, got ${describe(value)}`);
      return null;
    }
    return value;
  }

  text(value: unknown, path: string): string | null {
    if (typeof value !== 'string') {
      this.add(path, `expected a string, got ${describe(value)}`);
      return null;
    }
    return value;
  }

  /**
   * `firstCompletedAt` and `lastPlayedLevel`. Not `value === null ? null :
   * this.text(...)` -- sprint-8b stage 1, the same fix `nullableInt` (8a
   * stage 3) already got and this method did not: `text()` itself returns
   * `null` to mean "invalid", indistinguishable from a legitimate null
   * unless this checks the failure directly. Before this fix, an invalid
   * value (`lastPlayedLevel: 42`) added a fault to `c.faults` but returned
   * `null` all the same, which `checkGameProgress`'s `=== undefined` guard
   * could not see as a failure -- the invalid document validated anyway,
   * with a stray fault silently carried in a result whose own `ok` said
   * `true`. Returning `undefined` on genuine failure makes that guard fire.
   */
  nullableText(value: unknown, path: string): string | null | undefined {
    if (value === null) return null;
    if (typeof value !== 'string') {
      this.add(path, `expected a string, or null, got ${describe(value)}`);
      return undefined;
    }
    return value;
  }

  /**
   * Not `value === null ? null : this.int(...)` -- `int()` itself returns
   * `null` to mean "invalid", which is indistinguishable from a legitimate
   * null here unless this checks the failure directly rather than
   * delegating through a value that already means two different things.
   */
  nullableInt(value: unknown, path: string, min: number): number | null | undefined {
    if (value === null) return null;
    if (typeof value !== 'number' || !Number.isInteger(value) || value < min) {
      this.add(path, `expected an integer >= ${String(min)}, or null, got ${describe(value)}`);
      return undefined;
    }
    return value;
  }

  /**
   * An array of strings -- `LevelRecord.completedGoalSets`, sprint-8b
   * stage 1: content-derived `goalSetKey` identities, no longer indices.
   */
  stringArray(value: unknown, path: string): string[] | null {
    if (!Array.isArray(value)) {
      this.add(path, `expected an array, got ${describe(value)}`);
      return null;
    }
    const out: string[] = [];
    let ok = true;
    for (const [i, entry] of (value as unknown[]).entries()) {
      if (typeof entry !== 'string') {
        this.add(`${path}[${String(i)}]`, `expected a string, got ${describe(entry)}`);
        ok = false;
        continue;
      }
      out.push(entry);
    }
    return ok ? out : null;
  }

  int(value: unknown, path: string, min: number, max = Number.MAX_SAFE_INTEGER): number | null {
    if (typeof value !== 'number' || !Number.isInteger(value)) {
      this.add(path, `expected an integer, got ${describe(value)}`);
      return null;
    }
    if (value < min || value > max) {
      this.add(path, `expected an integer in ${String(min)}..${String(max)}, got ${String(value)}`);
      return null;
    }
    return value;
  }
}

function checkSettings(value: unknown, path: string, c: Checker): Settings | null {
  if (!isRecord(value)) {
    c.add(path, `expected an object, got ${describe(value)}`);
    return null;
  }
  const sound = c.bool(value.sound, `${path}.sound`);
  const music = c.bool(value.music, `${path}.music`);
  const reducedMotion = c.bool(value.reducedMotion, `${path}.reducedMotion`);
  if (sound === null || music === null || reducedMotion === null) return null;
  return { sound, music, reducedMotion };
}

function checkLevelRecord(value: unknown, path: string, c: Checker): LevelRecord | null {
  if (!isRecord(value)) {
    c.add(path, `expected an object, got ${describe(value)}`);
    return null;
  }
  const completed = c.bool(value.completed, `${path}.completed`);
  const completedGoalSets = c.stringArray(value.completedGoalSets, `${path}.completedGoalSets`);
  const bestScore = c.int(value.bestScore, `${path}.bestScore`, 0);
  const bestTimeMs = c.nullableInt(value.bestTimeMs, `${path}.bestTimeMs`, 0);
  const attempts = c.int(value.attempts, `${path}.attempts`, 0);
  const firstCompletedAt = c.nullableText(value.firstCompletedAt, `${path}.firstCompletedAt`);
  if (completed === null || completedGoalSets === null || bestScore === null || attempts === null) {
    return null;
  }
  if (bestTimeMs === undefined || firstCompletedAt === undefined) return null;
  return { completed, completedGoalSets, bestScore, bestTimeMs, attempts, firstCompletedAt };
}

function checkGameProgress(value: unknown, path: string, c: Checker): GameProgress | null {
  if (!isRecord(value)) {
    c.add(path, `expected an object, got ${describe(value)}`);
    return null;
  }
  const levelsValue = value.levels;
  if (!isRecord(levelsValue)) {
    c.add(`${path}.levels`, `expected an object, got ${describe(levelsValue)}`);
    return null;
  }
  const levels: Record<string, LevelRecord> = {};
  let levelsOk = true;
  for (const [levelId, recordValue] of Object.entries(levelsValue)) {
    const record = checkLevelRecord(recordValue, `${path}.levels.${levelId}`, c);
    if (record === null) {
      levelsOk = false;
      continue;
    }
    levels[levelId] = record;
  }
  const lastPlayedLevel = c.nullableText(value.lastPlayedLevel, `${path}.lastPlayedLevel`);
  if (!levelsOk || lastPlayedLevel === undefined) return null;
  return { levels, lastPlayedLevel };
}

function checkInProgress(value: unknown, path: string, c: Checker): InProgress | null | undefined {
  if (value === null) return null;
  if (!isRecord(value)) {
    c.add(path, `expected an object or null, got ${describe(value)}`);
    return undefined;
  }
  const gameId = c.text(value.gameId, `${path}.gameId`);
  const levelId = c.text(value.levelId, `${path}.levelId`);
  const savedAt = c.text(value.savedAt, `${path}.savedAt`);
  // `state` is opaque to the shell (save.ts) -- anything present is accepted.
  if (gameId === null || levelId === null || savedAt === null) return undefined;
  return { gameId, levelId, state: value.state, savedAt };
}

/**
 * `PROG-021`, `PROG-022`. The version check is the caller's, via
 * `checkSaveSchemaVersion` -- this only knows how to say a document is
 * malformed, not that it is from the future.
 */
export function validateSaveDocument(value: unknown): Validated<SaveDocument> {
  const c = new Checker();

  if (!isRecord(value)) {
    c.add('', `expected an object, got ${describe(value)}`);
    return { ok: false, faults: c.faults };
  }

  const schemaVersion = c.int(value.schemaVersion, 'schemaVersion', 1);
  const createdAt = c.text(value.createdAt, 'createdAt');
  const updatedAt = c.text(value.updatedAt, 'updatedAt');
  const settings = checkSettings(value.settings, 'settings', c);

  const gamesValue = value.games;
  const games: Record<string, GameProgress> = {};
  let gamesOk = true;
  if (!isRecord(gamesValue)) {
    c.add('games', `expected an object, got ${describe(gamesValue)}`);
    gamesOk = false;
  } else {
    for (const [gameId, progressValue] of Object.entries(gamesValue)) {
      const progress = checkGameProgress(progressValue, `games.${gameId}`, c);
      if (progress === null) {
        gamesOk = false;
        continue;
      }
      games[gameId] = progress;
    }
  }

  const inProgress = checkInProgress(value.inProgress, 'inProgress', c);

  if (
    schemaVersion === null ||
    createdAt === null ||
    updatedAt === null ||
    settings === null ||
    !gamesOk ||
    inProgress === undefined
  ) {
    return { ok: false, faults: c.faults };
  }

  return {
    ok: true,
    value: { schemaVersion, createdAt, updatedAt, settings, games, inProgress },
  };
}

/** `PROG-021`. `now` is injectable so a test can assert on the timestamp it produced. */
export function createDefaultSaveDocument(
  now: () => string = () => new Date().toISOString(),
): SaveDocument {
  const timestamp = now();
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    createdAt: timestamp,
    updatedAt: timestamp,
    // No requirement fixes these; sound and music default on, reduced motion off,
    // because that is the more common preference to have to turn off rather than
    // on. The OS-level `prefers-reduced-motion` signal is `platform/motion.ts`'s
    // concern, applied on top of this once a settings screen exists to show it.
    settings: { sound: true, music: true, reducedMotion: false },
    games: {},
    inProgress: null,
  };
}
